import { CSSProperties } from 'react';
import { IconName } from '@shared/components/Icon';
import styles from './AskMark.module.css';

interface Props {
  /** When true, the stars twinkle (used in the waiting/thinking state) and render green. */
  animated?: boolean;
  size?: number;
}

/**
 * The Ask mark — the one asset every Ask surface identifies itself with. It is currently the
 * Coast `sparkle`, and is expected to be replaced by a more branded asset, so everything that
 * shows it goes through this module: swapping the mark is an edit here and nowhere else.
 *
 * It exists in two forms because the surfaces need two different things, and both have to
 * change together:
 *
 * - `AskMark`, below: the standalone mark, the only form that animates.
 * - `ASK_MARK_ICON`: the `Icon` name, for anything taking an `icon` or `name` prop (buttons,
 *   menu items, `IconAvatar`).
 */
export const ASK_MARK_ICON: IconName = 'sparkle';

/**
 * Size for the mark sitting inline with body copy — the thinking indicator, the tool-call
 * lines, the replace warning. Generous, because the glyph is thin outlined strokes with
 * transparent margin inside its own viewBox, so it reads smaller than it measures.
 */
export const ASK_MARK_INLINE_SIZE = 40;

/**
 * The mark as its own element (four outlined stars). Static and Coast-purple by default; when
 * `animated`, it turns Coast-green and each star twinkles on a staggered, infrequent loop
 * (`transform-box: fill-box` scales each about its own center). Honors
 * `prefers-reduced-motion`.
 *
 * Drawn inline rather than through `Icon` because the animation addresses each star
 * separately, which needs the paths in the document.
 */
export function AskMark({ animated = false, size = 86 }: Props) {
  const style: CSSProperties = { width: size, height: size };
  return (
    <svg
      className={[styles.mark, animated && styles.animated].filter(Boolean).join(' ')}
      style={style}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <g className={styles.star}>
        <path
          d="M10 15C10.6566 15 11.3068 14.8707 11.9134 14.6194C12.52 14.3681 13.0712 13.9998 13.5355 13.5355C13.9998 13.0712 14.3681 12.52 14.6194 11.9134C14.8707 11.3068 15 10.6566 15 10C15 11.3261 15.5268 12.5979 16.4645 13.5355C17.4021 14.4732 18.6739 15 20 15"
          stroke="currentColor"
          strokeWidth="2"
          strokeMiterlimit="10"
        />
        <path
          d="M10 15C10.6566 15 11.3068 15.1293 11.9134 15.3806C12.52 15.6319 13.0712 16.0002 13.5355 16.4645C13.9998 16.9288 14.3681 17.48 14.6194 18.0866C14.8707 18.6932 15 19.3434 15 20C15 18.6739 15.5268 17.4021 16.4645 16.4645C17.4021 15.5268 18.6739 15 20 15"
          stroke="currentColor"
          strokeWidth="2"
          strokeMiterlimit="10"
        />
      </g>
      <g className={[styles.star, styles.delayed1].join(' ')}>
        <path
          d="M20 25C21.3261 25 22.5979 24.4732 23.5355 23.5355C24.4732 22.5979 25 21.3261 25 20C25 21.3261 25.5268 22.5979 26.4645 23.5355C27.4021 24.4732 28.6739 25 30 25"
          stroke="currentColor"
          strokeWidth="2"
          strokeMiterlimit="10"
        />
        <path
          d="M20 25C21.3261 25 22.5979 25.5268 23.5355 26.4645C24.4732 27.4021 25 28.6739 25 30C25 28.6739 25.5268 27.4021 26.4645 26.4645C27.4021 25.5268 28.6739 25 30 25"
          stroke="currentColor"
          strokeWidth="2"
          strokeMiterlimit="10"
        />
      </g>
      <g className={[styles.star, styles.delayed2].join(' ')}>
        <path
          d="M11 27H15"
          stroke="currentColor"
          strokeWidth="2"
          strokeMiterlimit="10"
          strokeLinecap="square"
        />
        <path
          d="M13 25V29"
          stroke="currentColor"
          strokeWidth="2"
          strokeMiterlimit="10"
          strokeLinecap="square"
        />
      </g>
      <g className={[styles.star, styles.delayed3].join(' ')}>
        <path
          d="M25 13H29"
          stroke="currentColor"
          strokeWidth="2"
          strokeMiterlimit="10"
          strokeLinecap="square"
        />
        <path
          d="M27 11V15"
          stroke="currentColor"
          strokeWidth="2"
          strokeMiterlimit="10"
          strokeLinecap="square"
        />
      </g>
    </svg>
  );
}
